import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { createEnrollmentCharacteristic } from '../../api/enrollment-characteristics/create-enrollment-characteristic'
import { updateEnrollmentCharacteristic } from '../../api/enrollment-characteristics/update-enrollment-characteristic'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import EnrollmentCharacteristicsForm from '../../components/pages/enrollment-characteristics/enrollment-characteristics-form'
import {
  getCharacteristicName,
  getCharacteristicTranslation,
  mapEnrollmentCharacteristicToInput
} from '../../components/pages/enrollment-characteristics/utils'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { TranslationLocale } from '../../lib/types/enums/translation/translation.enum'
import { EnrollmentCharacteristic } from '../../lib/types/models/enrollment-characteristics/enrollment-characteristics'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const EnrollmentCharacteristicsPage = () => {
  const tableRef = useRef<DynamicTableRef<EnrollmentCharacteristic>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    EnrollmentCharacteristic
  >(['edit', 'create'])

  const updateEnrollmentCharacteristics = useMutation({
    mutationFn: updateEnrollmentCharacteristic,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Caractère modifié avec succès')
    }
  })

  const createEnrollmentCharacteristics = useMutation({
    mutationFn: createEnrollmentCharacteristic,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Caractère créé avec succès')
    }
  })

  const columns: Column<EnrollmentCharacteristic>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true,
      cell: (data) => data.row.original.id
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      cell: (data) => getCharacteristicName(data.row.original)
    },
    {
      accessorKey: 'nameEn',
      header: 'NOM (Anglais)',
      cell: (data) => getCharacteristicTranslation(data.row.original, TranslationLocale.EN) ?? '-'
    },
    {
      accessorKey: 'nameDe',
      header: 'NOM (Allemand)',
      cell: (data) => getCharacteristicTranslation(data.row.original, TranslationLocale.DE) ?? '-'
    },
    {
      accessorKey: 'nameEs',
      header: 'NOM (Espagnol)',
      cell: (data) => getCharacteristicTranslation(data.row.original, TranslationLocale.ES) ?? '-'
    },

    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Caractère" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<EnrollmentCharacteristic>
        ref={tableRef}
        title="Caractère"
        columns={columns}
        deleteMessage={{
          name: 'Caractère',
          attributeName: 'id',
          deleteOne: (data) => `Caractère "${getCharacteristicName(data)}"`,
          deleteMany: (data) =>
            `Caractère "${data.map((row) => getCharacteristicName(row)).join('", "')}"`
        }}
        selectable
        allowSearchParam
        endpoint={API.enrollmentCharacteristics.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      {/* Edit */}
      {selectedData && (
        <Modal
          icon={SettingIcon}
          isOpen={modalState.edit}
          title="Modifier un caractère"
          hideActions
          onCancel={() => toggleModal('edit')}
        >
          <EnrollmentCharacteristicsForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={mapEnrollmentCharacteristicToInput(selectedData)}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              isLoading: updateEnrollmentCharacteristics.isPending,
              onConfirm: async (values) => {
                await updateEnrollmentCharacteristics.mutateAsync({
                  id: selectedData.id,
                  ...values
                })
              }
            })}
          />
        </Modal>
      )}

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer un caractère"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <EnrollmentCharacteristicsForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            isLoading: createEnrollmentCharacteristics.isPending,
            onConfirm: async (values) => {
              await createEnrollmentCharacteristics.mutateAsync(values)
            }
          })}
        />
      </Modal>
    </div>
  )
}

export default EnrollmentCharacteristicsPage
